import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { Icon } from "@/components/Icon";
import { developmentApi } from "@/lib/api";
import { Modal } from "@/components/Modal";
import { Textarea } from "@/components/Textarea";
import { Input } from "@/components/Input";
import { Select } from "@/components/Select";
import { PrioritySelect } from "@/components/PrioritySelect";
import {
    SearchableMultiSelect,
    type MultiSelectGroup,
} from "@/components/SearchableMultiSelect";
import { getDevelopmentUsers, type DevelopmentUser } from "@/pages/Developments/developmentUsers";

interface Development {
    id: number;
    title: string;
    description: string;
    priority: string;
    assigned_id?: number | null;
    assigned?: { id?: number } | null;
    client_id?: number;
    client?: { id: number; name: string; email: string };
    subcategories?: Array<{ id: number }>;
}

interface EditDevelopmentModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: (data: Development) => void;
    development: Development | null;
    clientOnly?: boolean;
}

export const EditDevelopmentModal: React.FC<EditDevelopmentModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
    development,
    clientOnly = false,
}) => {
    const { t } = useTranslation("global");
    const { isIt } = useAuth();
    const [title, setTitle] = useState<string>("");
    const [description, setDescription] = useState<string>("");
    const [priority, setPriority] = useState<string>("medium");
    const [assignedId, setAssignedId] = useState<number | null>(null);
    const [clientId, setClientId] = useState<number | null>(null);
    const [categories, setCategories] = useState<MultiSelectGroup[]>([]);
    const [subcategoryIds, setSubcategoryIds] = useState<number[]>([]);
    const [users, setUsers] = useState<DevelopmentUser[]>([]);
    const [loadingUsers, setLoadingUsers] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    // Populate fields when development changes
    useEffect(() => {
        if (development) {
            setTitle(development.title ?? "");
            setDescription(development.description ?? "");
            setPriority(development.priority ?? "medium");
            setAssignedId(development.assigned_id ?? development.assigned?.id ?? null);
            setClientId(
                development.client_id ?? development.client?.id ?? null
            );
            setSubcategoryIds(
                development.subcategories?.map((item) => item.id) ?? []
            );
            setError(null);
        }
    }, [development]);

    useEffect(() => {
        if (isOpen && categories.length === 0) {
            developmentApi
                .categories()
                .then((response) => {
                    if (response.result) {
                        setCategories(response.data);
                    }
                })
                .catch(() => undefined);
        }
    }, [isOpen, categories.length]);

    useEffect(() => {
        if (isOpen && isIt && users.length === 0) {
            setLoadingUsers(true);
            getDevelopmentUsers()
                .then(setUsers)
                .catch(() => undefined)
                .finally(() => setLoadingUsers(false));
        }
    }, [isOpen, isIt, users.length]);

    const clientOptions = useMemo(() => {
        const list = users
            .filter((u) => u.roles?.some((role) => {
                const normalizedRole = (typeof role === "string"
                    ? role
                    : role.tag || role.name || "").toLowerCase();

                return ["client", "development", "developments"].includes(normalizedRole);
            }))
            .map((u) => {
                return {
                    value: u.id,
                    label: u.name,
                    subtitle: `${u.email} · ${t("roles.tags.client")}`,
                };
            });

        return [
            {
                value: 0,
                label: t("developments.modal_new.client_none"),
            },
            ...list,
        ];
    }, [users, t]);

    const assigneeOptions = useMemo(() => [
        { value: 0, label: t("developments.detail.unassigned") },
        ...users.filter((user) => user.roles?.some((role) => (
            typeof role === "string" ? role.toLowerCase() === "it" :
                (role.tag || role.name || "").toLowerCase() === "it"
        ))).map((user) => ({
            value: user.id,
            label: user.name,
            subtitle: user.email,
        })),
    ], [users, t]);

    const handleSubmit = async (): Promise<void> => {
        if (!clientOnly && (!title.trim() || !description.trim())) {
            setError(t("developments.modal_new.validation_required"));
            return;
        }

        if (!development) {
            return;
        }

        setLoading(true);
        setError(null);
        try {
            const payload: {
                title: string;
                description: string;
                priority: string;
                assigned_id: number | null;
                subcategory_ids: number[];
                client_id?: number | null;
            } = {
                title: title.trim(),
                description: description.trim(),
                priority,
                assigned_id: assignedId,
                subcategory_ids: subcategoryIds,
            };

            if (isIt) {
                payload.client_id = clientId && clientId > 0 ? clientId : null;
            }

            const res = await developmentApi.update(development.id, payload);

            if (res.result) {
                onSuccess(res.data);
                onClose();
            } else {
                setError(
                    res.message || t("developments.modal_edit.error_update")
                );
            }
        } catch (err) {
            setError(
                err instanceof Error
                    ? err.message
                    : t("developments.modal_new.error_unexpected")
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={clientOnly ? t("developments.modal_new.field_client") : t("developments.modal_edit.title")}
            maxWidth="lg"
            onSubmit={handleSubmit}
            submitLabel={t("developments.modal_edit.save_button")}
            submittingLabel={t("developments.modal_edit.saving")}
            isSubmitting={loading}
            submitIcon={<Icon value="save" size={15} />}
        >
            {error && (
                <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-xs rounded-xl flex items-center space-x-2">
                    <Icon value="alertCircle" size={16} className="shrink-0" />
                    <span>{error}</span>
                </div>
            )}

            {!clientOnly && <Input
                required
                label={t("developments.modal_new.field_title")}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t("developments.modal_new.title_placeholder")}
            />}

            {/* Description */}
            {!clientOnly && <div>
                <Textarea
                    required
                    rows={4}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    label={t("developments.modal_new.field_desc")}
                    placeholder={t("developments.modal_new.desc_placeholder")}
                    fullscreenTitle={t("developments.modal_edit.title")}
                />
            </div>}

            {/* Client Select (visible for IT / Admin) */}
            {isIt && (
                <div>
                    <label className="block text-xs font-bold text-slate-700 dark:text-zinc-300 mb-1">
                        {t("developments.modal_new.field_client")}
                    </label>
                    <Select<number>
                        searchable
                        clearable
                        value={clientId ?? 0}
                        onChange={(val: number) =>
                            setClientId(val && val > 0 ? val : null)
                        }
                        options={clientOptions}
                        placeholder={t(
                            "developments.modal_new.client_placeholder"
                        )}
                        searchPlaceholder={t(
                            "developments.modal_new.client_placeholder"
                        )}
                        className="w-full"
                        disabled={loadingUsers}
                    />
                </div>
            )}

            {!clientOnly && <PrioritySelect
                value={priority}
                onChange={setPriority}
                label={t("developments.modal_new.field_priority")}
            />}

            {!clientOnly && <Select<number>
                label={t("developments.detail.assigned")}
                value={assignedId ?? 0}
                onChange={(value) => setAssignedId(value > 0 ? value : null)}
                options={assigneeOptions}
                searchable
                disabled={loadingUsers}
                full
            />}

            {!clientOnly && <SearchableMultiSelect
                label={t("developments.modal_new.field_categories")}
                groups={categories}
                value={subcategoryIds}
                onChange={setSubcategoryIds}
                placeholder={t("developments.modal_new.categories_placeholder")}
            />}
        </Modal>
    );
};
